var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useCompanyStore } from '../../../../../application/stores';
import { useFormValidationStore } from '../../../../../application/stores/formValidation.store';
import { getQuestionnaireByServiceIdAndType } from '../../../../../application/usecases/getOtsQuestionnaire';
import Loader from '../../../../../components/common/Loaders/Loader';
import { OperativeTemplate } from '../../../../../components/common/Templates/OperativeTemplate';
import { useFormTranslator } from '../../../../../hooks/useFormTranslations';
import { AnswerProcessorService, FormEvaluatorService, OTsService } from '../../../../../infrastructure/services';
import { QuestionRenderer } from '../../CommonComponents';
import GenericModal from '../../../../../components/common/Modals/GenericModal/GenericModal';
//TODO: [RR] Test
export const OtForm = () => {
    const { id, name: otName } = useParams();
    const otId = Number(id);
    const { t } = useTranslation();
    const translator = useFormTranslator();
    const { validateField, isSectionValid: checkSectionValid } = useFormValidationStore();
    const { company } = useCompanyStore();
    const [formState, setFormState] = useState(null);
    const [answers, setAnswers] = useState({});
    const processor = new AnswerProcessorService();
    const evaluator = new FormEvaluatorService();
    const otService = new OTsService();
    const navigate = useNavigate();
    const [isEnabled, setIsEnabled] = useState(true);
    const [openModalExit, setOpenModalExit] = useState(false);
    const prevVisibleRef = useRef(new Set());
    useEffect(() => {
        (() => __awaiter(void 0, void 0, void 0, function* () {
            const { form, generatedActivos } = yield getQuestionnaireByServiceIdAndType(1, company, "PDD");
            const activosAnswers = generatedActivos.map((activo, index) => ({
                id: `generated-${activo.id}`,
                value: activo.id.toString(),
                variable: activo.id.toString(),
                translationKey: activo.name,
                order: index
            }));
            form.questionnaire.sections.forEach((section) => {
                section.questions.forEach((question) => {
                    if (question.variableName.includes("_actives") &&
                        Array.isArray(question.answers) &&
                        question.answers.length === 0) {
                        question.answers = activosAnswers;
                    }
                });
            });
            setFormState(form);
        }))();
    }, []);
    const handleAnswerChange = useCallback((variable, value, question) => {
        var _a;
        if (!question)
            return;
        let newValue = value;
        if (question.type === "multipleChoice") {
            const prev = answers[variable] || [];
            const diff = value.find((v) => !prev.includes(v)) ||
                prev.find((v) => !value.includes(v));
            newValue = processor.processMultiSelectAnswer(question, prev, diff);
        }
        setAnswers((prev) => (Object.assign(Object.assign({}, prev), { [variable]: newValue })));
        const rule = (_a = question.validation) !== null && _a !== void 0 ? _a : {
            type: "custom-regex",
            regex: ".+",
            message: "Este campo es obligatorio",
        };
        validateField(variable, newValue, rule);
    }, [answers, processor, validateField]);
    const context = useMemo(() => ({
        activityId: company.activityId
    }), [company]);
    const allVisibleQuestions = useMemo(() => {
        if (!formState)
            return [];
        return formState.questionnaire.sections
            .flatMap((section) => section.questions.filter((q) => !q.displayConditions ||
            evaluator.evaluateDisplayConditions(q.displayConditions, answers, context)));
    }, [formState, answers, context]);
    const currentlyVisibleVars = useMemo(() => {
        return new Set(allVisibleQuestions.map((q) => q.variableName));
    }, [allVisibleQuestions]);
    useEffect(() => {
        const prevSet = prevVisibleRef.current;
        const justHidden = [];
        prevSet.forEach((varName) => {
            if (!currentlyVisibleVars.has(varName)) {
                justHidden.push(varName);
            }
        });
        if (justHidden.length > 0) {
            setAnswers((prev) => {
                const copy = Object.assign({}, prev);
                justHidden.forEach((varName) => {
                    delete copy[varName];
                    const detailsKey = `${varName}_details`;
                    if (copy.hasOwnProperty(detailsKey)) {
                        delete copy[detailsKey];
                    }
                });
                return copy;
            });
        }
        prevVisibleRef.current = currentlyVisibleVars;
    }, [currentlyVisibleVars]);
    const allAnswered = useMemo(() => {
        return allVisibleQuestions.every((q) => {
            const val = answers[q.variableName];
            if (q.type === "multipleChoice") {
                return Array.isArray(val) && val.length > 0;
            }
            if (q.type === "binaryChoice") {
                return val === "0" || val === "1";
            }
            return false;
        });
    }, [allVisibleQuestions, answers]);
    const validations = useMemo(() => {
        if (!formState)
            return [];
        const baseValidations = formState.questionnaire.sections.flatMap(section => section.questions
            .filter(q => !q.displayConditions ||
            evaluator.evaluateDisplayConditions(q.displayConditions, answers, context))
            .map(q => {
            var _a;
            return ({
                variableName: q.variableName,
                validation: (_a = q.validation) !== null && _a !== void 0 ? _a : {
                    type: "custom-regex",
                    regex: ".+",
                    message: t("PDDForm.validation.required", "Este campo es obligatorio"),
                },
            });
        }));
        return [...baseValidations];
    }, [answers, evaluator, context, t]);
    const sectionIsValid = checkSectionValid(validations);
    const handleFinish = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsEnabled(false);
        const otResponses = processor.transformToOtResponses(answers);
        const requestBody = processor.composeEipdRequestBody(otId, otResponses);
        yield otService.publishOt(company.companyId, otId, requestBody);
        navigate(-1);
    });
    if (!formState)
        return _jsx(Loader, { fullBlack: true });
    const canContinue = allAnswered && sectionIsValid && isEnabled;
    const handdleExit = () => {
        setOpenModalExit(false);
        navigate('/');
    };
    return (_jsxs(OperativeTemplate, Object.assign({ title: "PDDForm.title" }, { children: [_jsxs("div", Object.assign({ className: "container-xl OtForm" }, { children: [_jsx("h2", Object.assign({ className: "title-section-desktop" }, { children: otName })), _jsxs("form", { children: [formState.questionnaire.sections.map(section => {
                                return (_jsxs("div", Object.assign({ className: "mt-4 mb-4" }, { children: [_jsx("h3", Object.assign({ className: 'title-subsection' }, { children: t(`PDDForm.OTs.form.${section.id}.${section.translationKey}`) })), section.questions
                                            .filter(q => !q.displayConditions ||
                                            evaluator.evaluateDisplayConditions(q.displayConditions, answers, context))
                                            .map(q => (_jsx(QuestionRenderer, { question: q, value: answers[q.variableName], details: answers[q.variableName + "_details"], onChange: handleAnswerChange, translator: translator, disabled: false }, q.id)))] }), section.id));
                            }), _jsxs("div", Object.assign({ className: 'botonera-actions' }, { children: [_jsx(Button, Object.assign({ className: 'btn-exit', "data-testid": "exit-ots-btn", size: "small", variant: "text", onClick: () => setOpenModalExit(true) }, { children: t('common.btn.exit').toUpperCase() })), _jsx(Button, Object.assign({ disabled: !canContinue, size: "small", variant: "contained", onClick: handleFinish }, { children: t('PDDForm.OTs.finish').toUpperCase() }))] }))] })] })), _jsx(GenericModal, Object.assign({ open: openModalExit, title: t("PDDForm.OTs.modalExitTitle"), cssProps: { width: "450px" }, primaryAction: _jsx(Button, Object.assign({ onClick: () => handdleExit(), variant: "contained" }, { children: t("common.btn.exit").toUpperCase() })), secondaryAction: _jsx(Button, Object.assign({ onClick: () => setOpenModalExit(false), variant: "text" }, { children: t("common.btn.cancel").toUpperCase() })) }, { children: _jsx("p", { dangerouslySetInnerHTML: {
                        __html: t("PDDForm.OTs.modalExitOtFormText")
                    } }) }))] })));
};
